import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { BusinessSchema, LocationSchema } from "@/store/airloom-generated-rtk";
import { useBusinesses } from "@/store/hooks/useListBusinesses";
import { useEffect, useState, useRef } from "react";
import { useLocationsForBusiness } from "@/store/hooks/useListLocationsByBusiness";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/lib/store";
import { useLocation } from "react-router-dom";
import {
  setSelectedBusinessId,
  setSelectedLocationId,
} from "@/store/slices/locationIdSlice";
export interface BusinessDropdownProps {
  defaultBusinessId: string;
  defaultLocationId: string;
}
export const BusinessDropdown = ({
  defaultBusinessId,
  defaultLocationId,
}: BusinessDropdownProps) => {
  const selectedLocationId = useSelector(
    (state: RootState) => state.businessAndLocation.selectedLocationId,
  );
  const selectedBusinessId = useSelector(
    (state: RootState) => state.businessAndLocation.selectedBusinessId,
  );
  const { businesses, loading } = useBusinesses();
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSchema>();
  const [selectedLocation, setSelectedLocation] = useState<LocationSchema>();
  const userChangedRef = useRef(false);

  const { locations } = useLocationsForBusiness({
    businessId: selectedBusinessId ?? "",
  });
  const [businessesData, setBusinesses] = useState<BusinessSchema[]>([]);
  const [locationsData, setLocations] = useState<LocationSchema[]>([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (businesses) {
      setBusinesses(businesses);
    }
    if (locations) {
      setLocations(locations);
    }
  }, [businesses, locations]);
  useEffect(() => {
    if (!selectedBusinessId && businessesData?.length) {
      const storedBusinessId = sessionStorage.getItem("businessId");
      const storedBusiness = businessesData.find(
        (bus) => bus.id === storedBusinessId,
      );
      if (storedBusiness) {
        dispatch(setSelectedBusinessId(storedBusiness.id));
        setSelectedBusiness(storedBusiness);
      } else {
        const fallbackBusiness = businessesData.find(
          (bus) => bus.id === defaultBusinessId,
        );
        if (fallbackBusiness) {
          dispatch(setSelectedBusinessId(fallbackBusiness.id));
          setSelectedBusiness(fallbackBusiness);
        }
      }
    } else {
      const foundBusiness = businessesData.find(
        (bus) => bus.id === selectedBusinessId,
      );
      setSelectedBusiness(foundBusiness);
    }
  }, [businessesData, selectedBusinessId, dispatch]);
  useEffect(() => {
    if (selectedBusiness && locationsData?.length) {
      const storedLocationId = sessionStorage.getItem("locationId");
      const foundLocation = locationsData.find(
        (loc) => loc.id === storedLocationId,
      );
      if (foundLocation) {
        dispatch(setSelectedLocationId(foundLocation.id));
        setSelectedLocation(foundLocation);
      } else {
        const fallbackLocation = locationsData[0];
        if (fallbackLocation) {
          dispatch(setSelectedLocationId(fallbackLocation.id));
          setSelectedLocation(fallbackLocation);
        }
      }
    }
  }, [selectedBusiness, locationsData, dispatch]);
  useEffect(() => {
    if (selectedBusinessId) {
      sessionStorage.setItem("businessId", selectedBusinessId);
    }
  }, [selectedBusinessId]);
  useEffect(() => {
    if (selectedLocationId) {
      sessionStorage.setItem("locationId", selectedLocationId);
    }
  }, [selectedLocationId]);

  if (loading) {
    return <div>...loading</div>;
  }
  const handleSetBusinessId = (businessId: string) => {
    dispatch(setSelectedBusinessId(businessId));
  };
  const handleSetLocationId = (locationId: string) => {
    dispatch(setSelectedLocationId(locationId));
  };
  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 px-2 py-2 text-xs sm:text-sm min-w-0 hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent-dark rounded-md">
          <span className="truncate max-w-[80px] sm:max-w-[150px] block">
            {selectedBusiness?.name || "No Business"}
          </span>
          <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground dark:text-muted-foreground-dark" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>Businesses</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {businessesData?.map((business, index) => (
            <DropdownMenuItem
              key={index}
              onClick={() => {
                userChangedRef.current = true;
                setSelectedBusiness(business);
                handleSetBusinessId(business.id);
              }}
            >
              {business.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <span className="text-muted-foreground/60 dark:text-muted-foreground-dark/60">
        /
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 px-2 py-2 text-xs sm:text-sm min-w-0 hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent-dark rounded-md">
          <span className="truncate max-w-[80px] sm:max-w-[150px] block">
            {selectedLocation?.name || "No Location"}
          </span>
          <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground dark:text-muted-foreground-dark" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>Locations</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {locationsData?.map((location, index) => (
            <DropdownMenuItem
              key={index}
              onClick={() => {
                userChangedRef.current = true;
                setSelectedLocation(location);
                handleSetLocationId(location.id);
              }}
            >
              {location.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
